import React from 'react';
import { updateDoctor } from '../../logic/settings';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, FormControl, Row, Col, Container } from 'react-bootstrap';
import { clearStorage } from './../../libs/general';

function DoctorSchedule() {
	const navigate = useNavigate();
	const location = useLocation();
	const params = useParams();
	const [doc, setDoc] = React.useState(null);
	const [error, setError] = React.useState('');

	React.useEffect(() => {
		if (location && location.state && location.state.doc) {
			const docFromState = location.state.doc;
			
			// Ensure docId is present - use URL param as fallback if missing
			const docWithId = { ...docFromState };
			if (!docWithId.docId && params.docId) {
				docWithId.docId = params.docId;
			}
			
			// Log doctor information to debug
			console.log("Doctor from location state:", docWithId);
			
			 // Process schedule data to ensure proper types
			if (docWithId.preferences && docWithId.preferences.schedule) {
				docWithId.preferences.schedule = docWithId.preferences.schedule.map(sched => ({
					...sched,
					// Ensure all minute and hour values are strings and not blank or null
					startMinute: String(sched.startMinute || 0),
					endMinute: String(sched.endMinute || 0),
					startHour: String(sched.startHour || 0),
					endHour: String(sched.endHour || 0),
					minsPerAppointment: String(sched.minsPerAppointment || 30)
				}));
			}
			
			// Set the doctor in state
			setDoc(docWithId);
			
			if (!(docWithId.preferences && docWithId.preferences.schedule
				&& docWithId.preferences.schedule instanceof Array
				&& docWithId.preferences.schedule.length >= 1)) {
				// Wait for next render when doc state is set before adding schedule
				setTimeout(() => addSchedule(docWithId), 0);
			}
		}
	}, [location, params.docId]);

	const handleChange = (e, i) => {
		if (e && e.target && e.target.id && e.target.value !== undefined) {
			const field = e.target.id;
			const value = e.target.value;

			 // Deep clone the doctor object and its nested properties
			const updatedDoc = {...doc};
			
			// Ensure preferences and schedule exist
			if (!updatedDoc.preferences) updatedDoc.preferences = {};
			if (!updatedDoc.preferences.schedule) updatedDoc.preferences.schedule = [];
			
			// Ensure the schedule at index i exists
			if (!updatedDoc.preferences.schedule[i]) {
				updatedDoc.preferences.schedule[i] = {};
			}
			
			// Update the specific field
			updatedDoc.preferences.schedule[i][field] = value;
			
			// Update the state
			console.log(`Setting field ${field} to ${value} for schedule ${i}`);
			setDoc(updatedDoc);
		}
	}

	const handleSave = async (e) => {
		e.preventDefault();
		e.stopPropagation();

		// Ensure docId is present - use URL param as fallback if missing from doc object
		const docToSave = { ...doc };
		if (!docToSave.docId && params.docId) {
			docToSave.docId = params.docId;
		}

		// Log the doc object to see what we're sending
		console.log('Saving doctor data:', docToSave);
		
		if (!docToSave.docId) {
			handleError(new Error('Doctor ID is missing. Please reload the page.'));
			return;
		}

		try {
			const savedDoc = await updateDoctor(docToSave);
			clearStorage();

			if (savedDoc instanceof Error) {
				handleError(savedDoc);
			} else {
				navigate("/settings/doctors");
			}
		} catch (err) {
			handleError(err);
		}
	}

	const handleError = (e) => {
		console.log(e);
		setError(e);
	}

	const addSchedule = (docToUpdate) => {
		// Use the provided doc or the current state
		const updatedDoc = docToUpdate || doc;
		
		if (!updatedDoc) {
			console.error("Cannot add schedule: Doctor object is null");
			return;
		}
		
		console.log("Adding schedule to doctor:", updatedDoc.docId);

		// Create a new deep copy of the doctor object
		const newDoc = {...updatedDoc};
		
		// Ensure preferences object exists
		if (!newDoc.preferences) newDoc.preferences = {};
		
		// Ensure schedule array exists
		if (!newDoc.preferences.schedule) newDoc.preferences.schedule = [];
		
		// Log the current state before adding
		console.log("Current schedules count:", newDoc.preferences.schedule.length);
		
		// Add new schedule with default values
		newDoc.preferences.schedule.push({
			type: '',
			startDate: new Date().toISOString().split('T')[0],
			minsPerAppointment: '30',
			startHour: '9',
			startMinute: '0',
			endHour: '17',
			endMinute: '0'
		});
		
		// Log the new state
		console.log("After adding, schedules count:", newDoc.preferences.schedule.length);
		
		// Update state with the new doctor object
		setDoc(newDoc);
	}

	const deleteSchedule = (e, index) => {
		if (window.confirm('Are you sure you want to delete this schedule?')) {
			e.preventDefault();
			e.stopPropagation();

			// Create a deep copy of the doctor object
			const updatedDoc = {...doc};
			
			// Verify the schedule array and the index exist
			if (!(updatedDoc.preferences && updatedDoc.preferences.schedule
				&& updatedDoc.preferences.schedule instanceof Array
				&& index >= 0 && index < updatedDoc.preferences.schedule.length))
				return;

			// Log before deletion
			console.log(`Deleting schedule at index ${index}. Current count: ${updatedDoc.preferences.schedule.length}`);
			
			// Remove the schedule at index
			updatedDoc.preferences.schedule.splice(index, 1);
			
			// Log after deletion
			console.log(`After deletion, schedules count: ${updatedDoc.preferences.schedule.length}`);
			
			// Update state with the modified doctor object
			setDoc(updatedDoc);
		}
	}
	
	// Only render if doc is loaded
	if (!doc) return <div>Loading...</div>;

	return (
		<div>
			<div className="pages__back-button--container">
				<FontAwesomeIcon className="pages__back-button--icon" icon="chevron-left"></FontAwesomeIcon>
				<Link className="pages__back-button--text" to="/settings/doctors">Back</Link>
			</div>
			<header className="pages__header--container centered-text">
				<h1>Doctor's Schedule</h1>
			</header>
			<Container>
				<form autoComplete='on' onSubmit={handleSave} style={{ paddingBottom: '60px' }}>
					{
						(doc.preferences && doc.preferences.schedule
							&& doc.preferences.schedule instanceof Array
							&& doc.preferences.schedule.length >= 1) ?
							doc.preferences.schedule.map((sched, i) => {
								return (
									<fieldset className="pages__form--fieldset" key={i}>
										<Row>
											<Col xs={{ span: 6, offset: 3 }} sm={{ span: 6, offset: 3 }} md={{ span: 3, offset: 3 }} lg={{ span: 3, offset: 3 }}>
												<Form.Group className="mb-3">
													<Form.Label>Type:</Form.Label>
													<Form.Control
														as="select"
														id='type'
														required
														value={sched.type || ''}
														onChange={(e) => handleChange(e, i)}>
														<option value="">--</option>
														<option value="daily">Daily</option>
														<option value="weekly">Weekly</option>
														<option value="every2weeks">Every 2 Weeks</option>
														<option value="every3weeks">Every 3 Weeks</option>
														<option value="monthly">Monthly</option>
														<option value="every2months">Every 2 Months</option>
														<option value="every6months">Every 6 Months</option>
														<option value='yearly'>Yearly</option>
														<option value="disabled">Disabled</option>
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>Start Date</Form.Label>
													<Form.Control
														id='startDate'
														value={sched.startDate || ''}
														required
														onChange={(e) => handleChange(e, i)}
														type='date'>
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>Appointment Length&nbsp;<small>(5 - 120 mins)</small></Form.Label>
													<Form.Control
														id='minsPerAppointment'
														value={sched.minsPerAppointment || ''}
														required
														onChange={(e) => handleChange(e, i)}
														type='number'
														min='5'
														max='120'
														step='5'>
													</Form.Control>
												</Form.Group>
											</Col>
											<Col xs={{ span: 6, offset: 3 }} sm={{ span: 6, offset: 3 }} md={{ span: 3, offset: 0 }} lg={{ span: 3, offset: 0 }}>
												<Form.Group className="mb-3">
													<Form.Label>Start Hour&nbsp;<small>(0 - 23)</small></Form.Label>
													<Form.Control
														id='startHour'
														value={sched.startHour || '0'}
														required
														onChange={(e) => handleChange(e, i)}
														type='number'
														min='0'
														max='23'>
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>Start Min&nbsp;<small>(0 - 59)</small></Form.Label>
													<Form.Control
														id='startMinute'
														value={sched.startMinute || '0'}
														required
														onChange={(e) => handleChange(e, i)}
														type='number'
														min='0'
														max='59'>
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>End Hour&nbsp;<small>(0 - 23)</small></Form.Label>
													<Form.Control
														id='endHour'
														value={sched.endHour || '0'}
														required
														onChange={(e) => handleChange(e, i)}
														type='number'
														min='0'
														max='23'>
													</Form.Control>
												</Form.Group>
												<Form.Group className="mb-3">
													<Form.Label>End Minute&nbsp;<small>(0 - 59)</small></Form.Label>
													<Form.Control
														id='endMinute'
														value={sched.endMinute || '0'}
														required
														onChange={(e) => handleChange(e, i)}
														type='number'
														min='0'
														max='59'>
													</Form.Control>
												</Form.Group>
											</Col>
										</Row>
										<Row>
											<Col xs={{ span: 6, offset: 3 }} sm={{ span: 6, offset: 3 }} md={{ span: 6, offset: 3 }} lg={{ span: 6, offset: 3 }}
												className={
													doc.preferences.schedule.length - 1 === i ?
														"pages__form--col settings__list--actionIcons right-text no-border"
														: "pages__form--col settings__list--actionIcons right-text"
												}>
												<FontAwesomeIcon className="settings__list--delete " onClick={(e) => deleteSchedule(e, i)} icon="trash-alt"></FontAwesomeIcon>
											</Col>
										</Row>
									</fieldset>
								)
							})
							: null
					}
					<div className="pages__action-bar--container">
						<div onClick={() => addSchedule()} className="pages__action-bar--item">
							<FontAwesomeIcon icon="plus" />
						</div>
					</div>
					<div className="pages__form--error centered-text">
						{
							error && error instanceof Array ?
								error.map((err, i) => <p key={i}>{err.toString()}</p>)
								: error && error.message ? <p>{error.message}</p> : null
						}
					</div>
					<Row>
						<Col>
							<input className="save-button center-block small-button" type='submit' value='Save' />
						</Col>
					</Row>
				</form>
			</Container>
		</div>
	);
}

export default DoctorSchedule;